/**
 * Onboarding utility for generating auth tokens
 * Follows OnboardUser pattern from pkg/netrunner/clients/dex_client.go
 * Uses NonceManager for Ethereum-aligned nonce lifecycle management
 *
 * Flow with retry and nonce refresh:
 * 1. Get nonce from NonceManager (queries /router/v1/nonce/status/{address})
 * 2. Reserve nonce (off-chain operation)
 * 3. Build EIP-712 message with action="sys::onboard", owner, nonce (hex), timestamp, deadline (hex)
 * 4. Sign message with ethers.js EIP-712 signing - via signing/onboard.ts
 * 5. POST to /auth/v1/onboard - via signing/onboard.ts
 * 6. On success: Commit nonce (off-chain)
 * 7. On failure: Reject nonce (allows retry)
 * 8. Extract authToken from response - via signing/onboard.ts
 */

import { InfoClient } from "../api/info/InfoClient";
import mormSigner from "../signing/mormSigner";
import { OperationTypeOnboard } from "../types/operations";
import { submitOnboardRequest, OnboardResult } from "../signing/onboard";
import { int64ToHex } from "./math";
import { executeWithRetry, DefaultRetryConfigs } from "./retry";
import { shouldRefreshNonce } from "./errorDetection";
import { NonceManager } from "./nonceManager";

// Re-export OnboardResult for convenience
export type { OnboardResult } from "../signing/onboard";

/**
 * Onboard a wallet address and get auth token for WebSocket authentication
 * Uses NonceManager for Ethereum-aligned nonce lifecycle management
 * Implements Reserve → Execute → Commit/Reject pattern
 *
 * @param mnemonic - Mnemonic phrase for the wallet
 * @param baseURL - Base URL for API requests
 * @param username - Optional username
 * @param referralCode - Optional referral code
 * @returns OnboardResult with authToken if successful
 */
export async function onboardAndGetAuthToken(
  mnemonic: string,
  baseURL: string,
  username?: string,
  referralCode?: string,
): Promise<OnboardResult> {
  try {
    // Initialize mormSigner if not ready
    if (!mormSigner.isReady()) {
      await mormSigner.initialize();
    }

    // Generate wallet address from mnemonic
    const addressResult = await mormSigner.generateAddress(mnemonic, 0);
    if (!addressResult.success || !addressResult.address) {
      return {
        success: false,
        error: `Failed to generate address: ${addressResult.error}`,
      };
    }
    const walletAddress = addressResult.address.toLowerCase(); // Ensure lowercase

    // Create InfoClient and NonceManager
    const infoClient = new InfoClient({ baseURL });
    const nonceManager = new NonceManager(infoClient);

    // Track current nonce for retry logic
    let currentNonce: number | null = null;

    // Execute with retry, matching Go DefaultAuthConfig
    // MaxRetries: 10, BaseDelay: 500ms, MaxDelay: 3s, Multiplier: 2.0
    try {
      const result = await executeWithRetry(
        DefaultRetryConfigs.auth,
        async () => {
          // CRITICAL: Check next nonce from server on first attempt only
          // On retry attempts, use the incremented nonce from previous error
          console.log(
            `[Onboard] Attempt starting - currentNonce: ${currentNonce}`,
          );
          if (currentNonce === null) {
            // First attempt: query server for next nonce
            const nonceResult = await nonceManager.getExpectedNextNonce(
              walletAddress,
              false,
            );
            currentNonce = nonceResult.next_nonce;
            console.log(
              `[Onboard] First attempt: queried server, using next nonce: ${currentNonce}`,
            );
          } else {
            // Retry attempt: use the incremented nonce from previous error
            // This ensures we keep incrementing forward (e.g., 2 → 3 → 4 → ...) until it works
            console.log(
              `[Onboard] Retry attempt: using incremented nonce ${currentNonce} from previous failure`,
            );
          }

          // Reserve nonce (off-chain) - adds to pending list
          // This follows the Reserve/Commit/Reject pattern
          // CRITICAL: Keep incrementing nonce until validation succeeds
          let validationAttempts = 0;
          const maxValidationAttempts = 10; // Prevent infinite loop

          while (validationAttempts < maxValidationAttempts) {
            try {
              await nonceManager.validateNonce(
                walletAddress,
                currentNonce,
                true,
              );
              // Validation succeeded, break out of loop
              break;
            } catch (validateError: any) {
              validationAttempts++;

              // If validation fails (e.g., nonce already pending), increment and retry
              if (
                validateError.message.includes("already pending") ||
                validateError.message.includes("already used")
              ) {
                // Increment nonce and try again (keep incrementing until it works)
                currentNonce = currentNonce + 1;
                console.log(
                  `[Onboard] Nonce ${currentNonce - 1} already pending, incrementing to ${currentNonce}`,
                );

                if (validationAttempts >= maxValidationAttempts) {
                  throw new Error(
                    `Failed to find available nonce after ${maxValidationAttempts} attempts`,
                  );
                }
                // Continue loop to retry with incremented nonce
              } else {
                // Other validation errors, throw immediately
                throw validateError;
              }
            }
          }

          // Build EIP-712 message with current nonce
          // Per Go implementation: nonce and deadline must be hex strings, timestamp is number
          const timestamp = Math.floor(Date.now() / 1000); // Unix timestamp in seconds
          const deadline = timestamp + 3600; // 1 hour from now

          const message = {
            action: OperationTypeOnboard,
            owner: walletAddress,
            nonce: int64ToHex(currentNonce),
            timestamp: timestamp,
            deadline: int64ToHex(deadline),
          };

          // Submit request (will throw on error for retry mechanism)
          // CRITICAL: Wrap in try-catch to ensure nonce is rejected on any error
          let submitResult: OnboardResult;
          try {
            submitResult = await submitOnboardRequest({
              mnemonic,
              walletAddress,
              message,
              baseURL,
              username,
              referralCode,
              timestamp,
            });
          } catch (error: any) {
            // Reject nonce on any thrown error (before retry)
            // CRITICAL: After rejecting, increment nonce so next attempt uses next available nonce
            // Keep incrementing until it works
            if (currentNonce !== null) {
              const rejectedNonce = currentNonce;
              try {
                await nonceManager.rejectNonce(
                  walletAddress,
                  currentNonce,
                  true,
                );
                // Increment nonce for next attempt (e.g., if nonce 2 failed, try nonce 3, then 4, etc.)
                currentNonce = currentNonce + 1;
                console.log(
                  `[Onboard] Rejected nonce ${rejectedNonce}, incremented to nonce ${currentNonce} (will use on retry)`,
                );
              } catch (rejectError) {
                console.warn(
                  "[Onboard] Failed to reject nonce after thrown error:",
                  rejectError,
                );
                // Even if reject fails, increment to avoid retrying same nonce
                currentNonce = currentNonce + 1;
                console.log(
                  `[Onboard] Incremented nonce to ${currentNonce} (reject failed but incrementing anyway)`,
                );
              }
            } else {
              console.warn(
                `[Onboard] Error thrown but currentNonce is null, cannot reject/increment`,
              );
            }
            // Attach error details for retry callback
            (error as any).currentNonce = currentNonce;
            throw error; // Re-throw for retry mechanism
          }

          // Check if request failed
          if (!submitResult.success) {
            // Reject nonce on failure and increment for next attempt
            // CRITICAL: Keep incrementing nonce until it works (e.g., 2 → 3 → 4 → ...)
            if (currentNonce !== null) {
              const failedNonce = currentNonce;
              await nonceManager.rejectNonce(walletAddress, currentNonce, true);
              // Increment nonce to avoid retrying with same nonce
              // This ensures we keep incrementing until we find a nonce that works
              currentNonce = currentNonce + 1;
              console.log(
                `[Onboard] Request failed with nonce ${failedNonce}, rejected and incremented to ${currentNonce} (will keep incrementing until it works)`,
              );
            }

            const error = new Error(submitResult.error || "Onboarding failed");
            // Attach result for error detection
            (error as any).onboardResult = submitResult;
            throw error;
          }

          // Success! Commit nonce (off-chain)
          // This marks the nonce as used with TTL tracking (24h expiration)
          if (currentNonce !== null) {
            await nonceManager.commitNonce(walletAddress, currentNonce, true);
          }

          return submitResult;
        },
        // Should retry on any error (retry logic will handle nonce refresh)
        async (error: any) => {
          const errorStr = String(error).toLowerCase();
          const errorMessage = error?.message?.toLowerCase() || errorStr;
          const responseBody = error?.responseBody || error?.onboardResult;
          const responseError =
            responseBody?.error?.toLowerCase() ||
            responseBody?.message?.toLowerCase() ||
            "";
          const allErrorText =
            `${errorMessage} ${errorStr} ${responseError}`.toLowerCase();

          // CRITICAL: Check for "nonce already pending" error specifically FIRST
          // This error means the server has the nonce pending (on-chain) or used (off-chain within TTL)
          //
          // IMPORTANT: For off-chain operations (onboard), the server's /router/v1/nonce/status endpoint
          // returns on-chain nonce only. It does NOT account for off-chain nonces that are still within
          // the 24h TTL. So querying the server again won't help - we should just increment.
          //
          // For on-chain operations, we could query the server, but for off-chain (onboard), we just
          // increment until the server accepts it. The server's ValidateNonce will check both:
          // - On-chain pending nonces
          // - Off-chain used nonces (within 24h TTL)
          //
          // This must be checked BEFORE shouldRefreshNonce, because "nonce already pending"
          // is also a nonce error that would trigger shouldRefreshNonce
          if (allErrorText.includes("nonce already pending")) {
            // For off-chain operations (onboard), just increment - don't query server
            // The server's next_nonce doesn't account for off-chain nonces within TTL
            // The server will reject any nonce that's actually in use (on-chain pending or off-chain within TTL)
            // So we just keep incrementing until we find one that's available
            if (currentNonce !== null) {
              const previousNonce = currentNonce;
              currentNonce = currentNonce + 1;
              console.log(
                `[Onboard] Off-chain operation: nonce ${previousNonce} already pending/used, incrementing to ${currentNonce} (server will validate)`,
              );
            } else {
              // Fallback: if currentNonce is null, query server as starting point
              try {
                const nonceResult = await nonceManager.getExpectedNextNonce(
                  walletAddress,
                  false,
                );
                currentNonce = nonceResult.next_nonce;
                console.log(
                  `[Onboard] currentNonce was null, queried server for starting nonce: ${currentNonce}`,
                );
              } catch (queryError) {
                console.warn(
                  `[Onboard] Failed to query server nonce:`,
                  queryError,
                );
                // Last resort: start from 1
                currentNonce = 1;
              }
            }
            return true; // Retry with incremented nonce
          }

          // Check if this is a nonce-consuming error (but not "already pending" which we handled above)
          // Only clear currentNonce for errors that require full re-sync (e.g., "nonce too low", "nonce already used")
          if (shouldRefreshNonce(error)) {
            // On other nonce errors, clear currentNonce to force re-query (server may have consumed it)
            // Note: Nonce was already rejected in the try-catch above if submitOnboardRequest threw
            console.log(
              `[Onboard] Nonce-consuming error detected (not "already pending"), clearing currentNonce to force re-query`,
            );
            currentNonce = null;
            return true; // Retry with nonce refresh
          }

          // CRITICAL FIX: For non-nonce errors, preserve incremented nonce
          // The nonce was already rejected and incremented in the try-catch above
          // (e.g., nonce 2 failed → rejected → incremented to 3)
          // Don't clear currentNonce here - keep the incremented value for next attempt
          // This ensures we use the next nonce (3) instead of re-querying and potentially getting 2 again

          // For other errors, retry a few times (network errors, etc.)
          // But don't retry on non-retryable errors (e.g., invalid mnemonic)
          const nonRetryablePatterns = [
            "failed to generate address",
            "address mismatch",
            "invalid mnemonic",
          ];

          if (
            nonRetryablePatterns.some((pattern) => errorStr.includes(pattern))
          ) {
            return false; // Don't retry
          }

          return true; // Retry other errors (will use incremented currentNonce if set)
        },
      );

      // If we get here, retry succeeded - return the result
      return result;
    } catch (error: any) {
      // Retry exhausted or non-retryable error
      // Make sure to reject nonce if still pending
      if (currentNonce !== null) {
        try {
          await nonceManager.rejectNonce(walletAddress, currentNonce, true);
        } catch (rejectError) {
          // Ignore reject errors during cleanup
          console.warn(
            "[Onboard] Failed to reject nonce during cleanup:",
            rejectError,
          );
        }
      }

      return {
        success: false,
        error: error?.message || "Unknown error during onboarding",
      };
    }
  } catch (error: any) {
    return {
      success: false,
      error: error?.message || "Unknown error during onboarding",
    };
  }
}
